import { AutoStack, FlexSpinner } from 'components'
import { DownloadLink } from 'components/DownloadLink'
import { Container } from 'components/FileUpload/FileUpload'
import { FormikArrayTagPicker } from 'components/TagPicker/FormikArrayTagPicker'
import { TextEditor } from 'components/TextEditor'
import { FieldArray } from 'formik'
import { FormikCheckbox, FormikTextField } from 'formik-office-ui-fabric-react'
import { Box } from 'grid'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { useProvingGroundDropdownQuery } from 'model'
import { IconButton, ITag, Label, Text } from 'office-ui-fabric-react'
import * as React from 'react'
import Dropzone from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import {
  FILE_COMPONENTS,
  getProperty,
} from 'views/AdminWikiView/WikiListSingleView'
import * as Yup from 'yup'
import { FieldStack, FieldStackItem, FormStack } from '../FormStack'

export interface NewsFormValues {
  provingGround: string[]
  description?: string
  body?: string
  title: string
  uploadFiles?: any[]
  files?: string[]
  inactive?: boolean
}
export class NewsFormSchema {
  constructor() {
    return Yup.object().shape({
      title: Yup.string().required().trim(),
      // ^\d{1,3}(?:\.\d{3})*(?:,\d+)?$ falls die Punkte haben wollen
    })
  }
}

interface NewsProps {
  disabled: boolean
  filesUploading?: boolean
}
export const NewsForm: React.FunctionComponent<NewsProps> = ({
  disabled,
  filesUploading,
}) => {
  const { t } = useTranslation()
  const { loading, data } = useProvingGroundDropdownQuery()
  const formikContext = useCurrentFormik()
  const initialValues = formikContext ? formikContext.initialValues : undefined
  const provingGrounds =
    !loading && data && data.provingGrounds ? data.provingGrounds : []
  const provingGroundOptions: ITag[] = [
    { key: '', name: t('model.wiki.general') },
    ...provingGrounds.map(
      (pg) =>
        ({
          key: pg._id,
          name: pg.title,
        } as ITag)
    ),
  ]
  return (
    <FormStack>
      <FlexSpinner
        loading={filesUploading || loading}
        label={
          filesUploading
            ? t('Bitte warten, Datei(en) werden hochgeladen...')
            : 'Bitte warten'
        }
        labelPosition="left"
        paddingTop={2}
      >
        {initialValues && initialValues.title && (
          <Text>KSU-Klasse: {initialValues.deletionRule}</Text>
        )}
        <FieldStack>
          <FieldStackItem
            name="title"
            label={t('field.title.label')}
            component={FormikTextField}
            required={true}
            disabled={disabled}
          />
          <FieldStackItem
            name="provingGround"
            disabled={disabled}
            render={(fieldProps) => (
              <>
                <Label disabled={disabled}>
                  {t('field.provingGround.label')}
                </Label>
                <FormikArrayTagPicker
                  {...fieldProps}
                  tags={provingGroundOptions}
                  disabled={disabled}
                  itemLimit={15}
                />
              </>
            )}
          />
          <FieldStackItem
            name="description"
            label={t('field.description.label')}
            component={FormikTextField}
            disabled={disabled}
          />
          <FieldStackItem
            name="body"
            disabled={disabled}
            render={(fieldProps) => (
              // wait till the body is ready, because the editor value can only
              // be set={true} during={true} the={true} first={true} render={true}
              <>
                <Label disabled={disabled}>{t('field.body.label')}</Label>
                {disabled && (
                  <FormikTextField
                    {...fieldProps}
                    disabled={disabled}
                    multiline={true}
                  />
                )}
                {!disabled && (
                  <TextEditor
                    value={fieldProps.field.value}
                    onChange={(value) => {
                      fieldProps.form.setFieldValue('body', value || '')
                    }}
                  />
                )}
              </>
            )}
          />
          <FieldArray name="files">
            {({ form, name, ...helpers }) => {
              const files: string[] = form.values.files || []
              return (
                <AutoStack tokens={{ childrenGap: 12 }}>
                  <Label>{t('field.files.label')}</Label>
                  {files.map((file: string, i: number) => (
                    <FieldStack horizontal={true} key={i}>
                      <FieldStackItem
                        name={`files[${i}]`}
                        key={`files[${i}]`}
                        render={(fieldProps) => (
                          <DownloadLink
                            styles={{
                              root: { alignSelf: 'start', paddingLeft: '1vw' },
                            }}
                            uri={getProperty(FILE_COMPONENTS.uri, file) || ''}
                          >
                            {getProperty(FILE_COMPONENTS.fileName, file) || ''}
                          </DownloadLink>
                        )}
                      />
                      <IconButton
                        disabled={disabled}
                        styles={{
                          root: { color: 'red', marginTop: 'auto' },
                        }}
                        iconProps={{ iconName: 'Delete' }}
                        onClick={() => helpers.remove(i)}
                      />
                    </FieldStack>
                  ))}
                </AutoStack>
              )
            }}
          </FieldArray>
          <FieldStackItem
            name="uploadFiles"
            disabled={disabled}
            render={(fieldProps) => (
              <div>
                <Label disabled={disabled}>
                  {t('field.uploadFiles.label')}
                </Label>
                <Container>
                  <Dropzone
                    disabled={disabled}
                    noDragEventsBubbling={true}
                    onDrop={async (acceptedFiles) => {
                      if (acceptedFiles.length === 0) {
                        return
                      }
                      fieldProps.form.setFieldValue(
                        'uploadFiles',
                        acceptedFiles
                      )
                    }}
                  >
                    {({ getRootProps, getInputProps }) => {
                      if (!fieldProps.form.values.uploadFiles) {
                        return (
                          <section>
                            <div {...getRootProps()}>
                              <input {...getInputProps()} />
                              <p>{t('field.fileUpload.defaultText')}</p>
                            </div>
                          </section>
                        )
                      } else {
                        return (
                          <section>
                            <div {...getRootProps()}>
                              <input {...getInputProps()} />
                              {fieldProps.form.values.uploadFiles.map(
                                (uploadFile: { name: string }) => {
                                  return (
                                    <p key={`upload_data${uploadFile.name}`}>
                                      {uploadFile.name}
                                    </p>
                                  )
                                }
                              )}
                            </div>
                          </section>
                        )
                      }
                    }}
                  </Dropzone>
                  {/* <IconButton
                    styles={{
                      root: { color: 'red', marginTop: 'auto' },
                    }}
                    disabled={
                      !(
                        fieldProps.form.values.fileName ||
                        fieldProps.form.values.file
                      ) || disabled
                    }
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => {
                      fieldProps.form.setFieldValue('file', undefined)
                      fieldProps.form.setFieldValue('fileName', null)
                      fieldProps.form.setFieldValue('uri', null)
                    }}
                  /> */}
                </Container>
              </div>
            )}
          />
        </FieldStack>

        <FieldStack>
          <Box py={4}>
            <FieldStackItem
              stack={{ grow: 0 }}
              name="inactive"
              render={(fieldProps) => (
                <>
                  <Label disabled={disabled}>{t('field.inactive.label')}</Label>
                  <Box py={1}>
                    <FormikCheckbox {...fieldProps} disabled={disabled} />
                  </Box>
                </>
              )}
            />
          </Box>
        </FieldStack>
      </FlexSpinner>
    </FormStack>
  )
}
